import React, { useEffect, useRef, useState } from "react";

//schemas
import form from "./schema/form";
import { getValidation } from "./schema/validation";
import initialValues from "./schema/initialValues";

//external components
import { Box, Grid, CircularProgress } from "@mui/material";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
} from "@mui/material";
//components
import IALoadingButton from "../../../../components/IAButtons/IALoadingButton";

//pages
import Main from "./components/main";

// formik components
import { Formik, Form } from "formik";

//translation
import { useTranslation } from "react-i18next";

//hooks
import useQueryAuth from "../../../../hooks/useQueryAuth";
import useMutationAuth from "../../../../hooks/useMutationAuth";
import { useSnackbar } from "notistack";
import useAuth from "../../../../hooks/useAuth";
import useLazyQueryAuth from "../../../../hooks/useLazyQueryAuth";

import ContainerCard from "../../../../components/Cards/ContainerCard";
import {
	GET_TIME_ENTRY_BY_ID,
	UPDATE_TIME_ENTRY,
	CREATE_TIME_ENTRY,
	GET_ALL_TIME_ENTRIES_STATUS,
	DELETE_TIME_ENTRY_LIST,
} from "../../../../graphql/timeEntry";

//mnemonic values
import Mnemonic from "../../../../Mnemonics.json";

const TimesheetForm = ({
	timeSheetGuid,
	timeEntryGuid,
	synced = false,
	refetchData,
	editMode = false,
	myTime = false,
	fromProfile = false,
	handleChangeTimeEntryGuid,
	handleSetEmployeeGuid,
	handleEditMode,
	employees = [],
	selectedTask = null,
	resetFields,
	fromTimesheet = false,
	fromDashboard = false,
	group = false,
	handleCloseTimeEntryModal,
	handleDeselect,
	taskBillable,
	statusToUpdate = null,
	employeeLoading = false,
	disabled = false,
	fromReport = false,
	fromSelectedTaskFromDashboard = false,
}) => {
	const {
		storeUserTenant,
		storeUserGuid,
		userRoles,
		isTimeEntryDescriptionMandatory,
		isTimeEntryApprovalMandatory,
	} = useAuth();
	const { isAdmin } = userRoles;

	const { t } = useTranslation();

	const { formField } = form;

	const {
		employee,
		task,
		date,
		duration,
		description,
		minutes,
		hours,
		project,
		billable,
	} = formField;

	const { enqueueSnackbar } = useSnackbar();

	const [formData, setFormData] = useState(initialValues);
	const [status, setStatus] = useState(null);
	const [tenant, setTenant] = useState(null);
	const [manager, setManager] = useState(null);
	const [userID, setUserID] = useState(null);
	const [projectPermission, setProjectPermission] = useState(null);
	const [projectGuid, setProjectGuid] = useState(null);
	const [open, setOpen] = useState(null);
	const [button1Subitting, setButton1Submitting] = useState(false);
	const [button2Subitting, setButton2Submitting] = useState(false);
	const saveAndClose = useRef(false);
	const [timeEntryStatus, setTimeEntryStatus] = useState(null);
	const formikValuesRef = useRef(null);
	const formikRef = useRef(null);
	const [fetchData, setFetchData] = useState(false);

	const { mfunction: getStauses, loading: statusesLoading } = useLazyQueryAuth(
		GET_ALL_TIME_ENTRIES_STATUS,
		"GetTimeEntriesStatus"
	);

	const handleEditClick = async (values, actions) => {
		if (statusesLoading) return; // Prevent calling while data is loading
		await getStauses(
			{
				variables: {
					model: {
						userID: storeUserGuid,
					},
				},

				isWait: true,
			},
			(response) => {
				const adjustedStatusGuid =
					response?.GetTimeEntriesStatus?.timeEntriesStatus?.find(
						(status) => status.mnemonic === "adjusted"
					)?.timeEntryStatusGuid || null;
				const rejectedStatusGuid =
					response?.GetTimeEntriesStatus?.timeEntriesStatus?.find(
						(status) => status.mnemonic === "rejected"
					)?.timeEntryStatusGuid || null;

				const approvedStatusGuid =
					response?.GetTimeEntriesStatus?.timeEntriesStatus?.find(
						(status) => status.mnemonic === "approved"
					)?.timeEntryStatusGuid || null;
				EditTimeEntryData(
					values,
					actions,
					adjustedStatusGuid,
					rejectedStatusGuid,
					approvedStatusGuid
				);
			}
		);
	};
	const { mfunction: deleteRecords, loading: deleteLoading } = useMutationAuth(
		DELETE_TIME_ENTRY_LIST,
		"DeleteTimeEntries"
	);
	const handleDelete = () => {
		deleteRecords(
			{
				variables: {
					model: {
						timeEntryGuids: [timeEntryGuid],
					},
				},
			},
			(response) => {
				handleChangeTimeEntryGuid(null);
				handleCloseTimeEntryModal();
				enqueueSnackbar(t("timeEntry.list.delete.success"), {
					variant: "success",
				});
			}
		);
	};
	const [confirmOpen, setConfirmOpen] = useState(false);

	const handleConfirmDelete = () => {
		setConfirmOpen(false);
		handleDelete();
	};
	useEffect(() => {
		const handleKeyDown = (event) => {
			// Check for Ctrl + S
			if (event.key === "s" && event.ctrlKey) {
				event.preventDefault(); // Prevent the default behavior
				formikRef.current?.handleSubmit(formikValuesRef.current); // Submit the form
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		// Clean up the event listener on unmount
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	const validation = getValidation(isTimeEntryDescriptionMandatory, myTime);

	useEffect(() => {
		if (editMode === false) {
			setFormData(initialValues);
			setStatus(null);
			setManager(null);
			setTenant(null);
			setUserID(null);
			setProjectGuid(null);
			synced = null;
			disabled = null;
		}
	}, [editMode]);

	const { loading: getDataByIDLoading } = useQueryAuth(
		GET_TIME_ENTRY_BY_ID,
		"GetTimeEntryByID",
		{
			variables: { id: timeEntryGuid },
			skip: timeEntryGuid === null || employeeLoading,
			isWait: true,
		},
		{
			onCompleted: (response) => {
				const data = response?.GetTimeEntryByID?.timeEntry || {};
				const totalDurationInHours = data?.duration;

				const totalHours = Math.floor(totalDurationInHours);
				const totalMinutes = Math.round(
					(totalDurationInHours - totalHours) * 60
				);
				setFormData({
					[employee.name]: data.employee || null,
					[task.name]: data.task || null,
					[duration.name]: totalDurationInHours || 0.0,
					[minutes.name]: totalMinutes.toString() || "0",
					[hours.name]: totalHours.toString() || "0",
					[description.name]: data.description || "",
					[project.name]: data.task?.ticket?.project || "",
					[billable.name]: data.billable,
					//we can go back to this if we want to bring back timezone difference
					// 	[date.name]:
					// 		data.startTime == null
					// 			? null
					// 			: new Date(formatDateTimeZone(data.startTime, false)),
					// });
					[date.name]:
						data.startTime == null ? null : new Date(data.startTime),
				});
				setStatus(data.timeSheet?.status);
				setTimeEntryStatus(data.timeEntryStatus);
				setTenant(data.employee?.tenantGuid);
				setManager(data.employee?.directManagerGuid);
				setUserID(data.employee?.employeeGuid);
				setProjectGuid(data.task?.ticket?.project?.projectGuid);
				if (handleSetEmployeeGuid !== undefined)
					handleSetEmployeeGuid(data.employee?.employeeGuid);
				setOpen(data.open);
				setProjectPermission(
					data.task?.ticket?.project?.projectPermissions
				);
				setFetchData(true);
			},
		}
	);

	const { cfunction: editTimeEntryData } = useMutationAuth(
		UPDATE_TIME_ENTRY,
		"UpdateTimeEntryByManager"
	);

	function EditTimeEntryData(
		values,
		actions,
		adjustedStatusGuid,
		rejectedStatusGuid,
		approvedStatusGuid
	) {
		if (saveAndClose.current === false) setButton1Submitting(true);
		else setButton2Submitting(true);
		if (!open && values[hours.name] === 0 && values[minutes.name] === 0) {
			enqueueSnackbar(t("time.error"), {
				variant: "error",
				autoHideDuration: 5000,
			});
			setButton1Submitting(false);
			setButton2Submitting(false);
		} else
			editTimeEntryData(
				{
					variables: {
						model: {
							userID: storeUserGuid,
							timeEntryGuid: timeEntryGuid,
							projectGuid: projectGuid,
							employeeGuid: values[employee.name]?.employeeGuid,
							description: values[description.name],
							taskGuid: values[task.name]?.taskGuid,
							date: new Date(values[date.name]).toDateString(),
							duration:
								parseFloat(values[hours.name]) +
								parseFloat(values[minutes.name] / 60 || 0),
							synced: synced ? synced : false,
							billable: values[billable.name],
							timeEntryStatusGuid:
								isTimeEntryApprovalMandatory === true &&
								(timeEntryStatus.timeEntryStatusGuid ===
									rejectedStatusGuid ||
									timeEntryStatus.timeEntryStatusGuid ===
										approvedStatusGuid)
									? adjustedStatusGuid
									: null,
						},
					},
				},
				(response) => {
					if (
						response["UpdateTimeEntryByManager"].errorCode !== null &&
						response["UpdateTimeEntryByManager"].errorCode !== ""
					) {
						enqueueSnackbar(
							t(response["UpdateTimeEntryByManager"].errorMessage),
							{
								variant: "error",
								autoHideDuration: 5000,
							}
						);

						setButton1Submitting(false);
						setButton2Submitting(false);
					} else {
						enqueueSnackbar(t("timeEntryForm.updatedSuccessfully"), {
							variant: "success",
						});
						sessionStorage.removeItem("unsavedChanges");
						if (handleDeselect !== undefined) handleDeselect(false);
						setTimeout(() => {
							if (saveAndClose.current === true)
								handleCloseTimeEntryModal();
						}, 200);
						if (refetchData) refetchData();
					}
					setButton1Submitting(false);
					setButton2Submitting(false);
					setFormData((prevData) => ({
						...prevData,
						[formField.billable.name]:
							response["UpdateTimeEntryByManager"]?.timeEntry
								?.billable || false,
					}));
				},
				(error) => {
					setButton1Submitting(false);
					setButton2Submitting(false);
				}
			);
	}

	const { cfunction: createTimeEntryData } = useMutationAuth(
		CREATE_TIME_ENTRY,
		"CreateTimeEntryByManager"
	);

	function CreateTimeEntryData(values, actions) {
		if (saveAndClose.current === false) setButton1Submitting(true);
		else setButton2Submitting(true);
		if (!open && !values[task.name]?.taskGuid) {
			enqueueSnackbar(t("timeEntryForm.task.requiredMessage"), {
				variant: "error",
				autoHideDuration: 5000,
			});
			// actions?.setSubmitting(false);
			setButton1Submitting(false);
			setButton2Submitting(false);
			return;
		}
		if (!open && values[hours.name] === 0 && values[minutes.name] === 0) {
			enqueueSnackbar(t("time.error"), {
				variant: "error",
				autoHideDuration: 5000,
			});
			// actions?.setSubmitting(false);
			setButton1Submitting(false);
			setButton2Submitting(false);
		} else
			createTimeEntryData(
				{
					variables: {
						model: {
							userID: storeUserGuid,
							employeeGuid: values[employee.name]?.employeeGuid,
							taskGuid: values[task.name]?.taskGuid,
							date: new Date(values[date.name]).toDateString(),
							description: values[description.name] || "",
							duration:
								parseFloat(values[hours.name]) +
								parseFloat(values[minutes.name] / 60 || 0),
							timeSheetGuid: timeSheetGuid,
							billable: values[billable.name],
						},
					},
				},
				(response) => {
					if (
						response["CreateTimeEntryByManager"].errorCode !== null &&
						response["CreateTimeEntryByManager"].errorCode !== ""
					) {
						enqueueSnackbar(
							t(response["CreateTimeEntryByManager"].errorMessage),
							{
								variant: "error",
								autoHideDuration: 5000,
							}
						);
						setButton1Submitting(false);
						setButton2Submitting(false);
					} else {
						enqueueSnackbar(t("timeEntryForm.createdSuccessfully"), {
							variant: "success",
						});
						sessionStorage.removeItem("unsavedChanges");
						if (refetchData) refetchData();
						if (saveAndClose.current === true)
							handleCloseTimeEntryModal();
						if (handleDeselect !== undefined) handleDeselect(false);
						if (handleEditMode && saveAndClose.current === false)
							handleEditMode(true);
						if (
							handleChangeTimeEntryGuid &&
							saveAndClose.current === false
						)
							handleChangeTimeEntryGuid(
								response["CreateTimeEntryByManager"]?.timeEntry
									?.timeEntryGuid
							);
						// if (fromDashboard === true) handleCloseTimeEntryModal(false)
					}
					// actions?.setSubmitting(false);
					setButton1Submitting(false);
					setButton2Submitting(false);
				},
				(error) => {
					// actions?.setSubmitting(false);
					setButton1Submitting(false);
					setButton2Submitting(false);
				}
			);
	}

	const handleSubmit = (values, actions) => {
		if (editMode) handleEditClick(values, actions);
		// EditTimeEntryData(values, actions);
		else CreateTimeEntryData(values, actions);
	};
	const mutualDisable = synced
		? true
		: isAdmin ||
		  (timeEntryGuid !== null &&
				projectPermission?.some(
					(perm) =>
						perm.resourceGuid === storeUserGuid &&
						perm.canManageTimeEntries === true
				))
		? false
		: disabled;
	return (
		<>
			{/* Confirmation Dialog */}
			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
				<DialogTitle>{t("cancel.confirmation.title")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("confirmation.message")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmDelete} color="error">
						{t("confirmation.delete")}
					</Button>
				</DialogActions>
			</Dialog>
			{(editMode && !fetchData) || getDataByIDLoading ? (
				<ContainerCard
					sx={{
						height: "50vh",
						width: "600px",
						border: "none",
						boxShadow: "none",
					}}
				>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						height="100%"
						width="100%"
					>
						<CircularProgress />
					</Box>
				</ContainerCard>
			) : (
				<Box mb={5}>
					<Grid
						container
						justifyContent="left"
						alignItems="stretch"
						sx={{ height: "100%" }}
						spacing={2}
					>
						<Grid item xs={12} sm={12} md={12} lg={12}>
							{/* <ContainerCard> */}
							<Formik
								innerRef={formikRef}
								key={`${form.formID}-form`}
								initialValues={
									editMode === true ? formData : initialValues
								}
								validationSchema={validation[0]}
								onSubmit={handleSubmit}
								enableReinitialize
							>
								{({
									values,
									errors,
									touched,
									setFieldValue,
									setTouched,
								}) => {
									formikValuesRef.current = values;
									return (
										<Form id={form.formID} autoComplete="off">
											<Box p={2}>
												<Box>
													<Main
														formData={{
															values,
															touched,
															formField: form.formField,
															errors,
															setFieldValue,
															setTouched,
															editMode,
															synced,
															disabled,
														}}
														employees={employees}
														selectedTask={selectedTask}
														resetFields={resetFields}
														status={status}
														tenant={tenant}
														manager={manager}
														userID={userID}
														fromProfile={fromProfile}
														fromTimesheet={fromTimesheet}
														myTime={myTime}
														group={group}
														handleSetEmployeeGuid={
															handleSetEmployeeGuid
														}
														fromDashboard={fromDashboard}
														setProjectGuid={setProjectGuid}
														taskBillable={taskBillable}
														fromReport={fromReport}
														fromSelectedTaskFromDashboard={
															fromSelectedTaskFromDashboard
														}
														mutualDisable={mutualDisable}
													/>
												</Box>

												{!synced &&
													status !==
														Mnemonic.TimesheetStatuses.Approved &&
													(userID == null ||
														userID === storeUserGuid ||
														(isAdmin &&
															(tenant
																? tenant === storeUserTenant
																: true)) ||
														manager === storeUserGuid) && (
														<Box
															mt={2}
															width="100%"
															display="flex"
															justifyContent={
																open ? "space-between" : "end"
															}
															alignItems={
																open ? "right" : "left"
															}
															marginLeft={open ? "-16px" : "0px"}
														>
															{!open ? (
																<></>
															) : (
																<IALoadingButton
																	disabled={
																		!open ||
																		disabled ||
																		button2Subitting ||
																		button1Subitting ||
																		deleteLoading
																	}
																	loading={deleteLoading}
																	type="button"
																	onClick={() => {
																		if (
																			!button2Subitting &&
																			!button1Subitting
																		) {
																			setConfirmOpen(true); // Open confirmation dialog
																		}
																	}}
																	label={t(
																		"timeEntryForm.cancel"
																	)}
																/>
															)}
															<Box
																display="flex"
																alignItems="center"
															>
																<IALoadingButton
																	disabled={
																		mutualDisable ||
																		button1Subitting ||
																		button2Subitting
																	}
																	loading={button1Subitting}
																	type="button"
																	label={t(
																		"timeEntryForm.save"
																	)}
																	onClick={() => {
																		if (!button1Subitting) {
																			handleSubmit(
																				formikValuesRef.current
																			);
																		}
																	}}
																/>
																<span
																	style={{
																		marginLeft: "10px",
																	}}
																>
																	<IALoadingButton
																		disabled={
																			mutualDisable ||
																			button2Subitting ||
																			button1Subitting
																		}
																		loading={button2Subitting}
																		type="button"
																		onClick={() => {
																			if (
																				!button2Subitting
																			) {
																				handleSubmit(
																					formikValuesRef.current
																				);
																				saveAndClose.current = true;
																			}
																		}}
																		label={t(
																			"timeEntryForm.save.close"
																		)}
																	/>
																</span>
															</Box>
														</Box>
													)}
											</Box>
										</Form>
									);
								}}
							</Formik>
							{/* </ContainerCard> */}
						</Grid>
					</Grid>
				</Box>
			)}
		</>
	);
};

export default TimesheetForm;
